import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Layers, BarChart3, Droplets, AlertTriangle, ShieldCheck } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useI18n } from "@/lib/i18n";

const CYAN  = "#00E5FF";
const GREEN = "#39FF14";
const RED   = "#FF3B30";
const AMBER = "#FFCC00";

export default function MaterialDashboardView({ baseLayers, conditions, baseMetrics }) {
  const { t } = useI18n();
  const [materials, setMaterials] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [metrics, setMetrics] = useState(baseMetrics);
  const isLight = conditions?._mode === "day";
  // Theme-aware palette so the material list and metric cards stay readable in
  // GÜNDÜZ mode (the global CSS flips bg-black → white).
  const panelText      = isLight ? "#1A202C" : "#FFFFFF";
  const panelMuted     = isLight ? "#4A5568" : "rgba(255,255,255,0.7)";
  const activeText     = isLight ? "#1E40AF" : "#00E5FF";
  const activeBg       = isLight ? "#DBEAFE" : "#00E5FF15";
  const activeBorder   = isLight ? "#1E40AF" : "#00E5FF";
  // Elite electric-cyan section header — distinct yet harmonious with the teal
  // palette used elsewhere in the Calculation tab.
  const headerLabel    = "#06B6D4";

  // Load materials catalog
  useEffect(() => {
    api.get("/materials").then(({ data }) => setMaterials(data)).catch(() => {});
  }, []);

  const selected = useMemo(() => materials.find((m) => m.id === selectedId), [materials, selectedId]);

  // When user picks a material → replace the insulation layer (3rd by default) and recompute
  useEffect(() => {
    if (!selected || !baseLayers?.length) {
      setMetrics(baseMetrics);
      return;
    }
    // Replace insulation slot (index 2 in default wall) with the selected material
    const newLayers = baseLayers.map((l, idx) =>
      idx === 2
        ? { ...l, name: selected.name, lambda_value: selected.lambda_value, mu: selected.mu ?? 1 }
        : l
    );
    api.post("/calculate", {
      layers: newLayers.map(({ name, thickness, lambda_value, mu }) =>
        ({ name, thickness, lambda_value, mu: mu ?? 1 })),
      ...conditions,
    }).then(({ data }) => setMetrics(data)).catch(() => {});
  }, [selected, baseLayers, baseMetrics, conditions]);

  // Build Glaser chart data
  const chartData = useMemo(() => {
    if (!metrics?.temperatures || !metrics?.vapor_pressure) return [];
    const T = metrics.temperatures;
    const Psat = metrics.saturation_pressure || [];
    const Pact = metrics.vapor_pressure;
    return T.map((t, i) => ({
      i,
      label: `L${i}`,
      temperature: +t.toFixed(2),
      Psat: Psat[i] !== undefined ? +Psat[i].toFixed(0) : null,
      Pact: Pact[i] !== undefined ? +Pact[i].toFixed(0) : null,
    }));
  }, [metrics]);

  const hasCondensation = metrics?.condensation_risk;
  const dew = metrics?.dew_point;
  const verdictColor = hasCondensation ? RED : GREEN;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-start" data-testid="material-dashboard-view">
      {/* LEFT — Material selector (aligned height with graphic, custom neon scrollbar) */}
      <div className="lg:col-span-1 border-2 border-[#00E5FF] bg-black p-3 flex flex-col"
        style={{ boxShadow: "0 0 14px #00E5FF33", maxHeight: 620 }}
        data-testid="material-dashboard-list">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4" style={{ color: headerLabel }}/>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] font-extrabold"
            style={{ color: headerLabel }}>
            {t("matdash.insulation", "Insulation Materials")}
          </h3>
          <span className="ml-auto font-mono text-[10px] font-bold px-2 py-0.5 rounded"
            style={{ color: "#00E5FF", border: "1px solid #00E5FF55", background: "#00E5FF11" }}
            data-testid="material-count-badge">
            {materials.length} adet
          </span>
        </div>
        <div className="flex-1 min-h-0 space-y-1 overflow-y-auto pr-1 neon-scroll">
          {materials.map((m) => {
            const active = selectedId === m.id;
            return (
              <button key={m.id}
                onClick={() => setSelectedId(active ? null : m.id)}
                data-testid={`mat-row-${m.id}`}
                className="w-full text-left px-3 py-2 transition-all"
                style={{
                  backgroundColor: active ? activeBg : "transparent",
                  borderLeft:      active ? `3px solid ${activeBorder}` : "3px solid transparent",
                  color:           active ? activeText : panelText,
                }}>
                <div className="font-bold text-sm" style={{ color: active ? activeText : panelText }}>{m.name}</div>
                <div className="font-mono text-[10px] mt-0.5" style={{ color: panelMuted }}>
                  λ = {m.lambda_value} W/mK · μ = {m.mu ?? 1} · {m.category}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* RIGHT — Glaser + Yoğuşma */}
      <div className="lg:col-span-2 flex flex-col gap-4">
        {/* Glaser chart */}
        <section className="border-2 border-[#00E5FF] bg-black"
          style={{ boxShadow: "0 0 14px #00E5FF33" }}
          data-testid="material-dashboard-glaser">
          <header className="bg-[#00E5FF] text-black px-4 py-2 flex items-center gap-2">
            <BarChart3 className="w-4 h-4"/>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] font-extrabold">
              Glaser Analiz Grafiği · Temperature &amp; Vapor Pressure
            </span>
          </header>
          <div className="rd-chart-container p-3" data-chart="true" style={{ height: 280, backgroundColor: '#000000' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 12, left: 0 }}>
                <CartesianGrid stroke="#374151"/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "#FFFFFF" }}/>
                <YAxis yAxisId="left"  tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "#00E5FF" }}
                  label={{ value: "T (°C)", angle: -90, position: "insideLeft", fill: "#00E5FF", fontSize: 11 }}/>
                <YAxis yAxisId="right" orientation="right"
                  tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "#FFCC00" }}
                  label={{ value: "P (Pa)", angle: 90, position: "insideRight", fill: "#FFCC00", fontSize: 11 }}/>
                <Tooltip contentStyle={{ background: "#000", border: "1px solid #00E5FF", color: "#FFFFFF" }}/>
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}/>
                <Line yAxisId="left"  type="monotone" dataKey="temperature" stroke="#00E5FF" name="Temperature"        strokeWidth={2.4} dot={{ r: 3 }} isAnimationActive={false}/>
                <Line yAxisId="right" type="monotone" dataKey="Psat"        stroke="#FFCC00" name="Psat (Saturation)"  strokeWidth={2}   dot={{ r: 2 }} strokeDasharray="4 2" isAnimationActive={false}/>
                <Line yAxisId="right" type="monotone" dataKey="Pact"        stroke="#39FF14" name="Pact (Actual)"      strokeWidth={2.2} dot={{ r: 3 }} isAnimationActive={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Yoğuşma analysis */}
        <section className="border-2 bg-black"
          style={{ borderColor: verdictColor, boxShadow: `0 0 14px ${verdictColor}55, inset 0 0 8px ${verdictColor}22` }}
          data-testid="material-dashboard-yogusma">
          <header className="text-black px-4 py-2 flex items-center gap-2" style={{ backgroundColor: verdictColor }}>
            <Droplets className="w-4 h-4"/>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] font-extrabold">
              Yoğuşma Analizi · Condensation Risk Assessment
            </span>
          </header>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              {hasCondensation
                ? <AlertTriangle className="w-8 h-8" style={{ color: verdictColor }}/>
                : <ShieldCheck    className="w-8 h-8" style={{ color: verdictColor }}/>}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-extrabold"
                  style={{ color: verdictColor }}>
                  Verdict
                </div>
                <div className="text-xl font-extrabold mt-0.5" style={{ color: verdictColor }}>
                  {hasCondensation ? "YOĞUŞMA RİSKLİ" : "YOĞUŞMA YOK · NO RISK"}
                </div>
                <div className="font-mono text-[12px] font-bold mt-1" style={{ color: panelText }}>
                  Dew Point = {dew !== undefined ? dew.toFixed(2) : "—"} °C
                  {selected && <span style={{ color: activeText }}> · using {selected.name}</span>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <Metric label="R total"   unit="m²K/W"  value={metrics?.total_r}   color={CYAN}  textColor={panelText} mutedColor={panelMuted}/>
              <Metric label="U value"   unit="W/m²K"  value={metrics?.u_value}    color={GREEN} textColor={panelText} mutedColor={panelMuted}/>
              <Metric label="Surface T" unit="°C"     value={metrics?.surface_temperature} color={AMBER} textColor={panelText} mutedColor={panelMuted}/>
              <Metric label="Dew Point" unit="°C"     value={dew}                 color={verdictColor} textColor={panelText} mutedColor={panelMuted}/>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Metric({ label, unit, value, color, textColor = "#FFFFFF", mutedColor = "rgba(255,255,255,0.7)" }) {
  const txt = (value === undefined || value === null || Number.isNaN(value))
    ? "—" : Number(value).toFixed(3);
  return (
    <div className="border-2 border-[#1F1F1F] bg-black px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-wider font-extrabold" style={{ color: textColor }}>{label}</div>
      <div className="font-mono font-extrabold mt-1" style={{ color, fontSize: "1.4rem", textShadow: `0 0 8px ${color}77` }}>
        {txt}
      </div>
      <div className="font-mono text-[9px]" style={{ color: mutedColor }}>{unit}</div>
    </div>
  );
}
